import {appendSsaiRoutingParam, isSsaiRelatedSample} from '../core/ssai/ssaiUtils';
import type {AdSample} from '../types/AdSample';
import type {Sample} from '../types/Sample';

import {globalScope} from './globalScope';
import {post} from './Http';
import {logger} from './Logger';
import {joinUrls} from './Utils';

export class AnalyticsCall {
  private readonly analyticsServerUrl: string;
  private readonly adAnalyticsServerUrl: string;

  constructor(baseUrl: string) {
    this.analyticsServerUrl = joinUrls(baseUrl, 'analytics');
    this.adAnalyticsServerUrl = joinUrls(baseUrl, 'analytics/a');
  }

  sendRequest(sample: Sample) {
    let analyticsUrl = this.analyticsServerUrl;
    if (isSsaiRelatedSample(sample)) {
      analyticsUrl = appendSsaiRoutingParam(analyticsUrl);
    }

    post(analyticsUrl, sample);
  }

  sendUnloadRequest(sample: Sample) {
    let analyticsUrl = this.analyticsServerUrl;
    if (isSsaiRelatedSample(sample)) {
      analyticsUrl = appendSsaiRoutingParam(analyticsUrl);
    }

    navigatorSendBeacon(analyticsUrl, sample);
  }

  sendAdRequest(adSample: AdSample) {
    let adAnalyticsUrl = this.adAnalyticsServerUrl;
    if (isSsaiRelatedSample(adSample)) {
      adAnalyticsUrl = appendSsaiRoutingParam(adAnalyticsUrl);
    }

    post(adAnalyticsUrl, adSample);
  }

  sendUnloadAdRequest(adSample: AdSample) {
    let adAnalyticsUrl = this.adAnalyticsServerUrl;
    if (isSsaiRelatedSample(adSample)) {
      adAnalyticsUrl = appendSsaiRoutingParam(adAnalyticsUrl);
    }

    navigatorSendBeacon(adAnalyticsUrl, adSample);
  }
}

function navigatorSendBeacon(url: string, data: unknown) {
  if (typeof navigator?.sendBeacon !== 'function') {
    beaconFallback(url, data);
    return;
  }

  try {
    const blobOptions: BlobPropertyBag = {
      // Safari historically had bugs where sendBeacon would silently fail with certain Content-Type values (anything
      // other than CORS-satellited types like text/plain, application/x-www-form-urlencoded, or multipart/form-data).
      // The common workaround is to wrap it in a Blob with type: 'text/plain' or send it as FormData.
      type: 'text/plain',
    };
    const blob = new Blob([JSON.stringify(data)], blobOptions);
    const success = globalScope.navigator.sendBeacon(url, blob);
    if (!success) {
      logger.error('Did not succeed to send analytics data using sendBeacon');
      beaconFallback(url, data);
    }
  } catch (e) {
    const message = 'Failed to send analytics data using sendBeacon';
    logger.error(message, e);
    beaconFallback(url, data);
  }
}

function beaconFallback(url: string, data: unknown) {
  // Modern browsers without sendBeacon or payload too large
  //
  // `fetch({ keepalive: true })` is a better fallback than sync XHR because:
  // - It's async and non-blocking
  // - It also survives page unload, just like sendBeacon()
  // - It supports custom headers, methods, and response handling
  // - Its limit is ~64KB (same ballpark as sendBeacon)
  if (typeof globalScope.fetch === 'function') {
    try {
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {'Content-Type': 'text/plain'},
        keepalive: true,
      }).catch((e) => {
        const message = 'Failed to send analytics data using fetch as a fallback for sendBeacon';
        logger.error(message, e);
      });
    } catch (e) {
      const message = 'Failed to send analytics data creating fetch request as a fallback for sendBeacon';
      logger.error(message, e);
    }

    return;
  }

  // otherwise use our post (mostly to cover IE 11, where fetch does not exist)
  post(url, data);
}
